"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGetStrategyValues = void 0;
const react_1 = require("react");
const react_2 = require("@apollo/client/react");
const ramda_1 = require("ramda");
const client_constants_1 = require("../../client.constants");
const client_utils_1 = require("../../client.utils");
const hooks_1 = require("../../hooks");
const get_strategy_values_query_1 = require("../get-strategy-values.query");
const values_utils_1 = require("../values.utils");
const models_1 = require("../../../models");
// merge the new part with an already loaded
const updateQuery = (previousResult, options) => {
    var _a, _b;
    const { edges: prevAttributes = [] } = (_a = previousResult.productTreeNode.labelValidation) !== null && _a !== void 0 ? _a : {};
    const { productTreeNode } = (_b = options === null || options === void 0 ? void 0 : options.fetchMoreResult) !== null && _b !== void 0 ? _b : {};
    if (!(productTreeNode === null || productTreeNode === void 0 ? void 0 : productTreeNode.labelValidation)) {
        return previousResult;
    }
    const _c = productTreeNode.labelValidation, { edges = [] } = _c, strategyLabels = __rest(_c, ["edges"]);
    if (!edges.length) {
        return {
            productTreeNode: Object.assign(Object.assign({}, previousResult.productTreeNode), productTreeNode),
        };
    }
    const newAttribute = edges[0].node;
    const changedAttributeIndex = prevAttributes.findIndex(({ node }) => node.label === newAttribute.label);
    const prevAttribute = prevAttributes[changedAttributeIndex];
    if (!prevAttribute) {
        return {
            productTreeNode: Object.assign(Object.assign({}, productTreeNode), { labelValidation: Object.assign(Object.assign({}, strategyLabels), { edges: [...prevAttributes, ...edges] }) }),
        };
    }
    const changedAttribute = {
        node: Object.assign(Object.assign({}, prevAttribute.node), { values: Object.assign(Object.assign({}, newAttribute.values), { edges: [...prevAttribute.node.values.edges, ...newAttribute.values.edges] }) }),
    };
    const newAttributes = (0, ramda_1.update)(changedAttributeIndex, changedAttribute, prevAttributes);
    return {
        productTreeNode: Object.assign(Object.assign({}, productTreeNode), { labelValidation: Object.assign(Object.assign({}, strategyLabels), { edges: newAttributes }) }),
    };
};
const fetchMoreOptions = { updateQuery };
const useGetStrategyValues = ({ productTreeNodeId, catalogId, attributeName, sortingItem = models_1.SortingItem.SortByTextAsc, options, pageSize = client_constants_1.PAGE_SIZE_LARGE, filter, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const sort = values_utils_1.sortingItemToStrategyValuesSortMap[sortingItem];
    const [getStrategyValues, { data, previousData, error, networkStatus, variables, fetchMore, refetch, called, }] = (0, react_2.useLazyQuery)(get_strategy_values_query_1.GET_STRATEGY_VALUES, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: (_a = options === null || options === void 0 ? void 0 : options.fetchPolicy) !== null && _a !== void 0 ? _a : 'cache-first',
    });
    const _j = (_e = (_d = (_c = (_b = data === null || data === void 0 ? void 0 : data.productTreeNode) === null || _b === void 0 ? void 0 : _b.labelValidation) === null || _c === void 0 ? void 0 : _c.edges[0]) === null || _d === void 0 ? void 0 : _d.node) !== null && _e !== void 0 ? _e : {}, { values } = _j, attribute = __rest(_j, ["values"]);
    const { pageInfo, edges = [] } = values !== null && values !== void 0 ? values : {};
    const pagination = (0, hooks_1.usePagination)({
        pageInfo,
        networkStatus,
        pageSize: client_constants_1.PAGE_SIZE_LARGE,
        fetchMore,
        fetchMoreOptions,
        lastVariables: variables,
    });
    // using useQuery skip: true will remove all previously uploaded data
    (0, react_1.useEffect)(() => {
        if (attributeName && productTreeNodeId && !(options === null || options === void 0 ? void 0 : options.skip)) {
            void getStrategyValues({
                variables: {
                    catalogId,
                    sort,
                    productTreeNodeId,
                    label: attributeName,
                    filter,
                    first: pageSize,
                    before: null,
                    after: null,
                    last: null,
                },
            });
        }
        // getStrategyValues is intentionally omitted from deps: Apollo's useLazyQuery returns a new
        // function reference on every render, which would cause this effect to re-fire on every
        // CatalogActivityProgress polling cycle and overwrite valid results when productTreeNodeId is
        // momentarily empty during a re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attributeName, catalogId, options === null || options === void 0 ? void 0 : options.skip, productTreeNodeId, sort, filter, pageSize]);
    const onRefetch = (variables) => refetch(Object.assign(Object.assign({}, variables), { first: client_constants_1.PAGE_SIZE_LARGE, before: null, after: null, last: null }));
    return {
        data: edges,
        attributeData: attribute,
        previousData: (_h = (_g = (_f = previousData === null || previousData === void 0 ? void 0 : previousData.productTreeNode.labelValidation) === null || _f === void 0 ? void 0 : _f.edges[0]) === null || _g === void 0 ? void 0 : _g.node) === null || _h === void 0 ? void 0 : _h.values.edges,
        nodeProductTotalCount: (data === null || data === void 0 ? void 0 : data.productTreeNode.productPresentationsNew.totalCount) || 0,
        isLoading: (0, client_utils_1.isQueryLoadingOrRefetching)(networkStatus),
        isRefetching: (0, client_utils_1.isQueryRefetching)(networkStatus),
        called,
        error,
        pagination,
        refetch: onRefetch,
    };
};
exports.useGetStrategyValues = useGetStrategyValues;
